'use client';
import { useLayoutEffect, useState, useMemo } from 'react';
import Dialog from '@ui/components/Dialog/Dialog';
import { cn } from '@ngg/storefront-utils';
import useElementDimensions from '@utils/hooks/useElementDimensions';
import useScroll from '@utils/useScroll';
import useWindowSize from '@utils/useWindowSize';
import { Transition } from '@headlessui/react';
import Icon from '../../icon/icon';
import {
  getNavigationCollectionByNameAction,
  getNavigationSublinksById,
} from '@app/actions/navigation/actions';
import Link from '../../Link/Link';
import {
  NavigationLinkFragment,
  NavigationSublinkFragment,
  SplashFragment,
} from '@business/gql/graphql';
import * as styles from './MenuDrawer.styles';
import { Maybe } from 'graphql/jsutils/Maybe';
import NavSplash from '@ui/components/blocks/NavSplash';
import { useMenuStore } from '@stores/menu.store';
import PrefetchLink from '@ui/components/Link/PrefetchLink';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@utils/queries/queryKeys';
import config from '@utils/config';
import useDictionary from '@utils/hooks/useDictionary';
import Paragraph from '@ui/components/Paragraph/Paragraph';

const MenuColumn = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn([styles.menuColumn(), className])}>{children}</div>;
};

const MenuLinkButton = ({
  active,
  title,
  onClick,
  slug,
}: {
  active?: boolean;
  title?: string;
  onClick: () => void;
  slug: string;
}) => {
  return (
    <Link
      href={slug}
      className={cn([styles.menuButton(), active && 'underline'])}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}>
      {title}
      <Icon name="rightArrow" color="black" size={12} />
    </Link>
  );
};

const MenuLink = ({
  title,
  slug,
  onClick,
  className,
  withArrow,
  color,
}: {
  title: string;
  slug: string;
  onClick: () => void;
  className?: string;
  withArrow?: boolean;
  color?: string | null;
}) => {
  return (
    <Link
      href={slug}
      className={cn([
        'flex items-center justify-between hover:underline focus:outline-none',
        color === 'White' && 'text-white',
        color === 'Red' && 'text-green-light',
        className,
      ])}
      onClick={onClick}>
      {title}
      {withArrow && <Icon name="rightArrow" color="black" size={12} />}
    </Link>
  );
};

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeNavigationElement: string | null;
};
export default function MenuDrawer(props: Props) {
  const { activeNavigationElement } = props;
  const dictionary = useDictionary({ keys: ['showAll'] });
  const { data } = useQuery({
    queryKey: [
      QueryKeys.navigationLinks,
      config.locale,
      activeNavigationElement,
    ],
    queryFn: () => {
      return getNavigationCollectionByNameAction({
        locale: config.locale,
        name: activeNavigationElement ?? '',
      });
    },
  });

  return (
    <MenuDrawerUI
      mainNavigationLinks={data?.sublinksCollection?.items}
      bottomNavigationLinks={data?.additionalNavigationLinksCollection?.items}
      splash={data?.highlight}
      locale={config.locale}
      open={props.open}
      setOpen={props.setOpen}
      showAllText={dictionary.showAll}
    />
  );
}
type PropsUI = Omit<Props, 'activeNavigationElement'> & {
  locale: string;
  mainNavigationLinks?: Array<NavigationLinkFragment | null>;
  bottomNavigationLinks?: Array<NavigationLinkFragment | null>;
  splash?: Maybe<SplashFragment>;
  showAllText: string;
};
const MenuDrawerUI = ({
  mainNavigationLinks,
  bottomNavigationLinks,
  splash,
  open,
  setOpen,
  locale,
  showAllText,
}: PropsUI) => {
  const queryClient = useQueryClient();
  const { topBarVisible, topBarHeight } = useMenuStore();
  const { height: navbarHeight } = useElementDimensions('navbar', 'offset');
  const { height: campaignBarHeight = 0 } = useElementDimensions(
    'campaign-bar',
    'offset',
  );
  const { scrollPosition } = useScroll();
  const { isBreakpoint } = useWindowSize('md');
  const offset = useMemo(() => {
    return navbarHeight && scrollPosition > navbarHeight
      ? topBarHeight - (topBarVisible ? topBarHeight : 0)
      : 0;
  }, [navbarHeight, scrollPosition, topBarVisible, topBarHeight]);

  const partOfNavbarVisible = useMemo(() => {
    return navbarHeight
      ? Math.max(0, navbarHeight - campaignBarHeight - offset)
      : 0;
  }, [navbarHeight, campaignBarHeight, offset]);

  const [subMenuLevel1Open, setSubMenuLevel1Open] = useState(false);
  const [subMenuLevel2Open, setSubMenuLevel2Open] = useState(false);

  const [subMenuLevel1Items, setSubMenuLevel1Items] = useState<
    NavigationSublinkFragment | undefined | null
  >(undefined);
  const [subMenuLevel2Items, setSubMenuLevel2Items] = useState<
    NavigationSublinkFragment | undefined | null
  >(undefined);

  const onClose = () => {
    setOpen(false);
    setSubMenuLevel1Open(false);
    setSubMenuLevel2Open(false);
  };

  useLayoutEffect(() => {
    const navbar = document.getElementById('navbar');
    // Headless UI Dialog adds inert attribute to sibling elements
    // which causes navbar to be ignored when this dialog is opened
    setTimeout(() => {
      navbar?.removeAttribute('inert');
      navbar?.removeAttribute('aria-hidden');
    }, 0);
  }, [open]);

  return (
    <>
      <Dialog
        disableTitle={true}
        title=""
        open={open}
        onClose={onClose}
        top={isBreakpoint ? partOfNavbarVisible : 0}
        dialogTransitionProps={{
          enter: `linear duration-300`,
          enterFrom: `opacity-0 -translate-x-[100%]`,
          enterTo: `opacity-100 translate-x-0`,
          leave: `linear duration-500`,
          leaveFrom: `opacity-100 translate-x-0`,
          leaveTo: `opacity-0 -translate-x-[100%]`,
        }}
        classNames={{
          panel: styles.dialogPanel(),
          panelHeader: styles.dialogPanelHeader(),
        }}>
        <div className={(styles.menuColumnContainer(), 'font-sans')}>
          <div
            className="flex h-full text-sm"
            style={{
              height: isBreakpoint
                ? `calc(100vh - ${partOfNavbarVisible}px)`
                : '100vh',
            }}>
            <MenuColumn className="z-20">
              <>
                {mainNavigationLinks?.map((link) => {
                  if (!link) return null;
                  if (link?.sublinksCollection?.total === 0) {
                    return (
                      <PrefetchLink
                        key={link.title}
                        className="grid justify-start hover:underline"
                        href={link?.internalLink?.slug || ''}
                        onClick={onClose}>
                        {link?.title}
                      </PrefetchLink>
                    );
                  }
                  return (
                    <MenuLinkButton
                      slug={link?.internalLink?.slug || ''}
                      active={
                        subMenuLevel1Open &&
                        subMenuLevel1Items?.title === link.title
                      }
                      key={link.title}
                      title={link?.title || ''}
                      onClick={async () => {
                        const result = await queryClient.fetchQuery({
                          queryKey: [
                            QueryKeys.navigationLinks,
                            locale,
                            link?.sys.id,
                          ],
                          queryFn: () => {
                            return getNavigationSublinksById({
                              locale,
                              id: link?.sys.id,
                            });
                          },
                        });
                        setSubMenuLevel2Open(false);
                        setSubMenuLevel1Items(result);
                        setSubMenuLevel1Open(true);
                      }}
                    />
                  );
                })}
                <section className={styles.menuColumnBottomSection()}>
                  {bottomNavigationLinks?.map((link) => {
                    if (!link) return null;
                    return (
                      <MenuLink
                        slug={link?.internalLink?.slug || ''}
                        key={link.title}
                        title={link.title || ''}
                        onClick={onClose}
                        color={link.linkColor}
                      />
                    );
                  })}
                  {splash && (
                    <div onClick={onClose} className="mb-2 mt-auto">
                      <NavSplash
                        key={splash.sys.id}
                        locale={locale}
                        backdrop
                        priority={false}
                        {...splash}
                      />
                    </div>
                  )}
                </section>
              </>
            </MenuColumn>
            <Transition
              style={{ zIndex: 10 }}
              as="section"
              enter="linear duration-300"
              enterFrom="-translate-x-[100%]"
              enterTo="translate-x-0"
              leave="linear duration-200"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-[100%]"
              show={!!subMenuLevel1Open}>
              <MenuColumn className="z-20">
                <div className={styles.menuColumnTitle()}>
                  {subMenuLevel1Items?.internalLink?.slug ? (
                    <div className="flex justify-between">
                      <PrefetchLink
                        className="hover:underline"
                        href={subMenuLevel1Items?.internalLink?.slug}
                        onClick={onClose}>
                        {subMenuLevel1Items?.title}
                      </PrefetchLink>
                      <PrefetchLink
                        className="font-normal hover:underline"
                        href={subMenuLevel1Items?.internalLink?.slug}
                        onClick={onClose}>
                        {showAllText}
                      </PrefetchLink>
                    </div>
                  ) : (
                    <div className="flex">
                      <Paragraph>{subMenuLevel1Items?.title}</Paragraph>
                    </div>
                  )}
                </div>

                {subMenuLevel1Items?.sublinksCollection?.items.map((item) => {
                  if (!item) return null;
                  if ((item.sublinksCollection?.total ?? 0) > 0) {
                    return (
                      <MenuLinkButton
                        slug={item?.internalLink?.slug || ''}
                        active={
                          subMenuLevel2Open &&
                          subMenuLevel2Items?.title === item?.title
                        }
                        key={item.title}
                        title={item.title || ''}
                        onClick={async () => {
                          const result = await queryClient.fetchQuery({
                            queryKey: [
                              QueryKeys.navigationLinks,
                              locale,
                              item?.sys.id,
                            ],
                            queryFn: () => {
                              return getNavigationSublinksById({
                                locale,
                                id: item?.sys.id,
                              });
                            },
                          });
                          setSubMenuLevel2Items(result);
                          setSubMenuLevel2Open(true);
                        }}
                      />
                    );
                  }
                  return (
                    <MenuLink
                      slug={item?.internalLink?.slug || ''}
                      key={item.title}
                      title={item.title || ''}
                      onClick={onClose}
                    />
                  );
                })}

                {subMenuLevel1Items?.highlight && (
                  <section
                    onClick={onClose}
                    className={styles.subMenuColumnBottomSection()}>
                    <NavSplash
                      key={subMenuLevel1Items?.highlight.sys.id}
                      locale={locale}
                      backdrop
                      priority={false}
                      {...subMenuLevel1Items?.highlight}
                    />
                  </section>
                )}
              </MenuColumn>
            </Transition>
            <Transition
              style={{ zIndex: 5 }}
              as="section"
              enter="linear duration-300"
              enterFrom="-translate-x-[100%]"
              enterTo="translate-x-0"
              leave="linear duration-200"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-[100%]"
              show={!!subMenuLevel2Open}>
              <MenuColumn>
                <div className={styles.menuColumnTitle()}>
                  {subMenuLevel2Items?.internalLink?.slug ? (
                    <div className="flex justify-between">
                      <PrefetchLink
                        className="hover:underline"
                        href={subMenuLevel2Items?.internalLink?.slug}
                        onClick={onClose}>
                        {subMenuLevel2Items?.title}
                      </PrefetchLink>
                      <PrefetchLink
                        className="font-normal hover:underline"
                        href={subMenuLevel2Items?.internalLink?.slug}
                        onClick={onClose}>
                        {showAllText}
                      </PrefetchLink>
                    </div>
                  ) : (
                    <div className="flex">
                      <Paragraph>{subMenuLevel2Items?.title}</Paragraph>
                    </div>
                  )}
                </div>
                {subMenuLevel2Items?.sublinksCollection?.items.map((item) => {
                  if (!item) return null;
                  return (
                    <MenuLink
                      slug={item?.internalLink?.slug || ''}
                      key={item.title}
                      title={item.title || ''}
                      onClick={onClose}
                    />
                  );
                })}

                {subMenuLevel2Items?.highlight && (
                  <section
                    onClick={onClose}
                    className={styles.subMenuColumnBottomSection()}>
                    <NavSplash
                      key={subMenuLevel2Items?.highlight.sys.id}
                      locale={locale}
                      backdrop
                      priority={false}
                      {...subMenuLevel2Items?.highlight}
                    />
                  </section>
                )}
              </MenuColumn>
            </Transition>
          </div>
        </div>
      </Dialog>
    </>
  );
};
